import React, { useState, useEffect } from "react";
import { useAddToCartMutation, useAddToCartupdateMutation, useDeleteFromCartMutation, useLazyCartViewQuery } from "../../redux/Api/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveCartCountHandler } from "../../redux/reducer/CartCountSlice";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [fetchCart] = useLazyCartViewQuery();
  const [deleteFromCart] = useDeleteFromCartMutation();
  const [cartCount, setCartCount] = useState(0);
  const [showRemovePopup, setShowRemovePopup] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cartlist] = useLazyCartViewQuery();
  const [cartUpdate] = useAddToCartupdateMutation();

  const user = JSON.parse(localStorage.getItem("user"));

  const fetchCartItems = async () => {
    if (!user?._id) return;
    
    try {
      const items = await fetchCart(user._id).unwrap();
      setCartItems(items);
      setCartCount(items.length);
    } catch (err) {
      console.error("Error fetching cart data:", err);
      toast.error("Error fetching cart items.");
    }
  };

  const updateCartQuantity = async (item, newQuantity) => {
    if (!user?._id || !item?.product?._id || newQuantity < 1) return;

    try {
      const payload = {
        productId: item.product._id,
        userId: user._id,
        quantity: newQuantity
      };

      await cartUpdate(payload).unwrap();
      await fetchCartItems(); // Refresh cart items
      toast.success("Cart updated successfully");
    } catch (err) {
      console.error("Error updating cart:", err);
      toast.error("Failed to update cart");
    }
  };

  const increaseQuantity = (item) => {
    const newQuantity = (item.quantity || 1) + 1;
    updateCartQuantity(item, newQuantity);
  };

  const decreaseQuantity = (item) => {
    if (item.quantity <= 1) return;
    const newQuantity = item.quantity - 1;
    updateCartQuantity(item, newQuantity);
  };

  useEffect(() => {
    if (user?._id) {
      fetchCartItems();
    } else {
      toast.error("Please log in first.");
    }
  }, []);

  const getcartlist = async () => {
    if (!user?._id) return;

    try {
      const res = await cartlist(user._id).unwrap();
      dispatch(saveCartCountHandler(res?.length || 0));
    } catch (err) {
      console.error("Error fetching cart list:", err);
    }
  };

  const handleRemove = async (item) => {
    if (!item?._id) {
      toast.error("Unable to remove item.");
      return;
    }

    try {
      await deleteFromCart(item._id).unwrap();
      await getcartlist();
      setCartItems((prevItems) => prevItems.filter((cartItem) => cartItem._id !== item._id));
      toast.success("Item removed successfully.");
    } catch (err) {
      console.error("Error removing item from cart:", err);
      toast.error("Error removing item from cart.");
    }
  };

  const confirmRemove = (item) => {
    handleRemove(item);
    setShowRemovePopup(null);
  };

  const cancelRemove = () => {
    setShowRemovePopup(null);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }
    navigate("/checkout", { 
      state: { 
        cartItems,
        totalAmount: totalPrice + 50 // Including delivery charges
      }
    });
  };

  const totalPrice = cartItems.reduce((acc, item) => {
    const price = item.product?.price || 0;
    const quantity = item.quantity || 1;
    return acc + (price * quantity);
  }, 0);

  return (
    <div className="p-5 mt-[130px] lg:mt-[250px]">
      <h1 className="text-3xl font-bold mb-6">Your Shopping Cart</h1>
      {cartItems.length === 0 ? (
        <p>Your cart is empty!</p>
      ) : (
        <div className="flex flex-col md:flex-row gap-5">
          <div className="md:w-2/3">
            {cartItems.map((item) => (
              <div
                key={item._id}
                className="flex flex-col md:flex-row shadow-md border border-gray-300 bg-white mb-4"
              >
                <div className="md:w-1/3 p-8 flex justify-center items-center">
                  <img
                    src={item.product?.image || "/placeholder.png"}
                    alt={item.product?.name || "Product image not available"}
                    className="w-full h-full object-cover rounded-lg shadow"
                  />
                </div>
                <div className="md:w-2/3 p-11">
                  <h3 className="text-xl font-bold mb-2">{item.product?.name || "N/A"}</h3>
                  <p className="text-gray-600 mb-4">{item.product?.description || "No description available"}</p>
                  <p className="font-bold text-lg mb-4">Rs {item.product?.price || 0}</p>
                  <div className="flex items-center mb-4">
                    <button
                      onClick={() => decreaseQuantity(item)}
                      className="bg-gray-200 text-gray-700 px-3 py-1 rounded-sm"
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <p className="px-4 py-1 bg-gray-100 border mx-2">
                      {item.quantity || 1}
                    </p>
                    <button
                      onClick={() => increaseQuantity(item)}
                      className="bg-gray-200 text-gray-700 px-3 py-1 rounded-sm"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => setShowRemovePopup(item)}
                    className="text-black hover:text-blue-500 text-lg underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="md:w-1/3 shadow-md border border-gray-300 bg-white p-5 h-fit">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            <p className="text-gray-600 mb-2">Subtotal: Rs {totalPrice.toFixed(2)}</p>
            <p className="text-gray-600 mb-4">Delivery Charges: Rs 50</p>
            <p className="font-bold text-lg mb-6">
              Total: Rs {(totalPrice + 50).toFixed(2)}
            </p>
            <button
              onClick={handleCheckout}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 w-full"
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}

      {showRemovePopup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg">
            <h2 className="text-lg font-bold mb-4">
              Are you sure you want to remove this item?
            </h2>
            <div className="flex justify-end">
              <button
                onClick={() => confirmRemove(showRemovePopup)}
                className="bg-red-500 text-white px-4 py-2 rounded mr-2 hover:bg-red-600"
              >
                Remove
              </button>
              <button
                onClick={cancelRemove}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;